import type { QuantaHeaders } from './auth';
import { quantaApi } from './quantaApi';

export interface VaultClaimIdentity {
  userId: string;
  vaultId: string;
  origin: string;
  network: 'mainnet' | 'testnet';
  vaultAddress: string;
  vaultPublicKey: string;
}
export const vaultClaimCode = (error: unknown) => (error as { response?: { data?: { code?: string } } })?.response?.data?.code;

export function vaultClaimContext(identity: VaultClaimIdentity, nonce: string) {
  return {
    operation: 'ducat:quanta:vault-claim:v1',
    origin: identity.origin,
    userId: identity.userId,
    vaultId: identity.vaultId.toLowerCase(),
    network: identity.network,
    vaultAddress: identity.vaultAddress,
    vaultPublicKey: (identity.vaultPublicKey.length === 66 ? identity.vaultPublicKey.slice(2) : identity.vaultPublicKey).toLowerCase(),
    nonce,
  };
}
export function vaultClaimMessage(identity: VaultClaimIdentity, nonce: string) {
  return `Ducat Quanta vault ownership\nOrigin: ${identity.origin}\nAuthorize this account to claim this vault and its Quanta rewards. This is not a login.\n${JSON.stringify(vaultClaimContext(identity, nonce), null, 2)}`;
}
export async function claimVaultAutomatically(
  userId: string,
  vaultId: string,
  headers: QuantaHeaders,
  network: VaultClaimIdentity['network'],
) {
  try {
    await quantaApi.post('/vault/claim', { userId, vaultId, network }, { headers });
    return { claimed: true };
  } catch (error) {
    if (vaultClaimCode(error) === 'VAULT_OWNERSHIP_REQUIRED') return { claimed: false };
    throw error;
  }
}

export async function proveVaultClaim(
  identity: VaultClaimIdentity,
  headers: QuantaHeaders,
  sign: (address: string, message: string) => Promise<string>,
  assertCurrent: () => void,
) {
  assertCurrent();
  const { data: response } = await quantaApi.post('/vault/claim/challenge', identity, { headers });
  const challenge = response.data;
  assertCurrent();
  if (
    typeof challenge.nonce !== 'string' ||
    !challenge.nonce ||
    !Number.isFinite(Date.parse(challenge.expiresAt)) ||
    Date.parse(challenge.expiresAt) <= Date.now()
  ) {
    throw new Error('Invalid vault ownership challenge');
  }
  const message = vaultClaimMessage(identity, challenge.nonce);
  const bytes = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(message));
  const hash = Array.from(new Uint8Array(bytes), (byte) => byte.toString(16).padStart(2, '0')).join('');
  if (
    challenge.message !== message ||
    challenge.hash !== hash ||
    JSON.stringify(challenge.context) !== JSON.stringify(vaultClaimContext(identity, challenge.nonce))
  ) {
    throw new Error('Vault ownership challenge does not match this account');
  }
  assertCurrent();
  const signature = await sign(identity.vaultAddress, message);
  assertCurrent();
  if (Date.parse(challenge.expiresAt) <= Date.now()) throw new Error('Vault ownership challenge expired');
  if (!signature) throw new Error('Vault ownership signature unavailable');
  await quantaApi.post('/vault/claim', { ...identity, nonce: challenge.nonce, signature }, { headers });
  assertCurrent();
}
